import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import { Code, User, ThumbsUp, Copy, Check, Search, Plus, X } from 'lucide-react';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import './Community.css';

export default function Community() {
    const { user } = useAuth();
    const [scripts, setScripts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const [showPostModal, setShowPostModal] = useState(false);
    const [newPost, setNewPost] = useState({ title: '', content: '', tags: '' });
    const [posting, setPosting] = useState(false);

    useEffect(() => {
        fetchCommunityScripts();
    }, []);

    const fetchCommunityScripts = async () => {
        setLoading(true);
        try {
            // Fetch shared scripts from team_shared_items or library_items marked as public
            const { data, error } = await supabase
                .from('team_shared_items')
                .select(`
                    *,
                    profiles:user_id (full_name, email)
                `)
                .eq('type', 'script')
                .order('created_at', { ascending: false })
                .limit(50);

            if (error) throw error;
            setScripts(data || []);
        } catch (error) {
            console.error('Error fetching community scripts:', error);
            // Fallback to empty array
            setScripts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = (id, content) => {
        navigator.clipboard.writeText(content);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handlePostScript = async () => {
        if (!user || !newPost.title.trim() || !newPost.content.trim()) return;

        setPosting(true);
        try {
            const { error } = await supabase
                .from('team_shared_items')
                .insert({
                    user_id: user.id,
                    type: 'script',
                    content: newPost.content.trim(),
                    context: {
                        title: newPost.title.trim(),
                        tags: newPost.tags.split(',').map(t => t.trim()).filter(Boolean),
                    },
                });

            if (error) throw error;

            setNewPost({ title: '', content: '', tags: '' });
            setShowPostModal(false);
            fetchCommunityScripts();
        } catch (error) {
            console.error('Error posting script:', error);
        } finally {
            setPosting(false);
        }
    };

    const filteredScripts = scripts.filter(script => {
        const query = searchQuery.toLowerCase();
        const title = script.context?.title?.toLowerCase() || '';
        const content = script.content?.toLowerCase() || '';
        const tags = (script.context?.tags || []).join(' ').toLowerCase();
        return title.includes(query) || content.includes(query) || tags.includes(query);
    });

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    };

    return (
        <>
            <Navbar />
            <main className="community">
                <div className="community__container">
                    <header className="community__header">
                        <div className="community__headerText">
                            <h1 className="community__title">Community Scripts</h1>
                            <p className="community__subtitle">
                                Share your scripts and discover code from other architects
                            </p>
                        </div>

                        <div className="community__actions">
                            <div className="community__search">
                                <Search size={18} />
                                <input
                                    type="text"
                                    placeholder="Search scripts..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            {user && (
                                <button
                                    className="community__postBtn"
                                    onClick={() => setShowPostModal(true)}
                                >
                                    <Plus size={18} />
                                    Share Script
                                </button>
                            )}
                        </div>
                    </header>

                    {loading ? (
                        <div className="community__loading">
                            <div className="community__spinner" />
                            <p>Loading community scripts...</p>
                        </div>
                    ) : filteredScripts.length === 0 ? (
                        <div className="community__empty">
                            <Code size={48} />
                            <h3>No scripts found</h3>
                            <p>Be the first to share a script with the community!</p>
                        </div>
                    ) : (
                        <div className="community__grid">
                            {filteredScripts.map((script) => (
                                <article key={script.id} className="community__card">
                                    <header className="community__cardHeader">
                                        <h3 className="community__cardTitle">
                                            {script.context?.title || 'Untitled Script'}
                                        </h3>
                                        <div className="community__cardMeta">
                                            <User size={14} />
                                            <span>{script.profiles?.full_name || script.profiles?.email?.split('@')[0] || 'Anonymous'}</span>
                                            <span className="community__cardDate">
                                                {formatDate(script.created_at)}
                                            </span>
                                        </div>
                                    </header>

                                    <pre className="community__code">
                                        <code>{script.content}</code>
                                    </pre>

                                    {script.context?.tags?.length > 0 && (
                                        <div className="community__tags">
                                            {script.context.tags.map((tag, i) => (
                                                <span key={i} className="community__tag">{tag}</span>
                                            ))}
                                        </div>
                                    )}

                                    <footer className="community__cardFooter">
                                        <button
                                            className="community__copyBtn"
                                            onClick={() => handleCopy(script.id, script.content)}
                                        >
                                            {copiedId === script.id ? (
                                                <><Check size={14} /> Copied!</>
                                            ) : (
                                                <><Copy size={14} /> Copy Code</>
                                            )}
                                        </button>
                                    </footer>
                                </article>
                            ))}
                        </div>
                    )}
                </div>

                {/* Post Script Modal */}
                {showPostModal && (
                    <div className="community__modal">
                        <div className="community__modalContent">
                            <header className="community__modalHeader">
                                <h2>Share a Script</h2>
                                <button onClick={() => setShowPostModal(false)}>
                                    <X size={20} />
                                </button>
                            </header>

                            <div className="community__form">
                                <div className="community__formGroup">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Auto-generate floor plans"
                                        value={newPost.title}
                                        onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                                    />
                                </div>

                                <div className="community__formGroup">
                                    <label>Code</label>
                                    <textarea
                                        placeholder="Paste your Python/Dynamo script here..."
                                        value={newPost.content}
                                        onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                                        rows={10}
                                    />
                                </div>

                                <div className="community__formGroup">
                                    <label>Tags (comma-separated)</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., revit, python, automation"
                                        value={newPost.tags}
                                        onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
                                    />
                                </div>

                                <button
                                    className="community__submitBtn"
                                    onClick={handlePostScript}
                                    disabled={posting || !newPost.title.trim() || !newPost.content.trim()}
                                >
                                    {posting ? 'Posting...' : 'Share with Community'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
            <Footer />
        </>
    );
}
